import React from 'react';
import PropTypes from 'prop-types';

import {Box, Typography, TextField, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio} from 'views/components';
import {Translate, translate, ClientAutocomplete, MarkdownEditor} from 'views/module';

const Details = (props) => {
  const {setIsStepValid, newJob, updateJobDetails} = props;

  const [isRegistered, setIsRegistered] = React.useState(newJob.client?.clientRn && newJob.client.clientRn !== 'null' ? 'yes' : 'no');
  const [selectedClient, setSelectedClient] = React.useState(newJob.client?.clientRn !== 'null' ? newJob.client : null);
  const [unregisteredClientName, setUnregisteredClientName] = React.useState(newJob.client?.clientRn === 'null' ? newJob.client?.clientName || '' : '');
  const [shortDescription, setShortDescription] = React.useState(newJob.description?.short || '');
  const [longDescription, setLongDescription] = React.useState(newJob.description?.long?.blob || '');

  // Save data to job object whenever state changes
  React.useEffect(() => {
    let clientData = null;

    if (isRegistered === 'yes' && selectedClient) {
      clientData = {
        clientRn: selectedClient.clientRn,
        clientName: selectedClient.clientName,
      };
    } else if (isRegistered === 'no' && unregisteredClientName) {
      clientData = {
        clientRn: 'null',
        clientName: unregisteredClientName,
      };
    }

    updateJobDetails(() => ({
      client: clientData,
      description: {
        short: shortDescription,
        long: {
          blob: longDescription,
          editorType: 'MARKDOWN',
        },
      },
    }));
  }, [selectedClient, unregisteredClientName, isRegistered, shortDescription, longDescription, updateJobDetails]);

  React.useEffect(() => {
    // Validate step - require client selection or name, short description, and long description
    const hasClient = isRegistered === 'yes' ? selectedClient !== null : unregisteredClientName.trim().length > 0;
    const isValid = isRegistered !== '' && hasClient &&
    shortDescription.trim().length > 0 &&
    longDescription.trim().length > 0;
    setIsStepValid(isValid);
  }, [setIsStepValid, isRegistered, selectedClient, unregisteredClientName, shortDescription, longDescription]);

  const handleIsRegisteredChange = (event) => {
    const value = event;
    setIsRegistered(value);
    setSelectedClient(null);
    setUnregisteredClientName('');
  };

  const handleClientChange = (newClient) => {
    setSelectedClient(newClient);
  };

  const handleUnregisteredClientNameChange = (value) => {
    setUnregisteredClientName(value);
  };

  const handleShortDescriptionChange = (event) => {
    setShortDescription(event);
  };

  const handleLongDescriptionChange = (value) => {
    setLongDescription(value);
  };

  return (
    <Box sx={{p: 2}}>
      <Typography variant="h4" gutterBottom sx={{textAlign: 'center', mb: 4}}>
        <Translate expression="career.create.Details.Title" />
      </Typography>

      <Box >
        <FormControl fullWidth>
          <FormLabel sx={{mb: 1}}>
            <Translate expression="career.create.Details.ClientLabel" />
          </FormLabel>
          <Box sx={{borderColor: 'divider', borderRadius: 1, px: 3}}>
            <RadioGroup value={isRegistered} onChange={handleIsRegisteredChange} label={''}>
              <FormControlLabel value="yes" control={<Radio label={translate('career.create.Details.ClientExistsLabel')} />} />
              <FormControlLabel value="no" control={<Radio label={translate('career.create.Details.UnregisteredClientLabel')} />} />
            </RadioGroup>
          </Box>
        </FormControl>
      </Box>

      {
        isRegistered === 'yes' &&
        <Box sx={{mb: 3}}>
          <FormControl fullWidth>
            <ClientAutocomplete
              label={translate('career.create.Details.ClientPlaceholder')}
              selectedClient={selectedClient}
              setSelectedClient={handleClientChange}
              statuses={['ACTIVE', 'LEAD', 'CG_SEARCH']}
            />
          </FormControl>
        </Box>
      }

      {
        isRegistered === 'no' &&
        <Box sx={{mb: 3}}>
          <FormControl fullWidth>
            <TextField
              value={unregisteredClientName}
              onChange={handleUnregisteredClientNameChange}
              variant="outlined"
              fullWidth
              label={translate('career.create.Details.UnregisteredClientPlaceholder')}
            />
          </FormControl>
        </Box>
      }

      <Box sx={{mb: 3}}>
        <FormControl fullWidth>
          <FormLabel sx={{mb: 1}}>
            <Translate expression="career.create.Details.ShortDescriptionLabel" />
          </FormLabel>
          <TextField
            multiline
            rows={3}
            value={shortDescription}
            onChange={handleShortDescriptionChange}
            variant="outlined"
            fullWidth
            placeholder={translate('career.create.Details.ShortDescriptionPlaceholder')}
          />
        </FormControl>
      </Box>

      <Box sx={{mb: 3}}>
        <FormControl fullWidth>
          <FormLabel sx={{mb: 1}}>
            <Translate expression="career.create.Details.LongDescriptionLabel" />
          </FormLabel>
          <MarkdownEditor
            value={longDescription}
            onChange={handleLongDescriptionChange}
            placeholder={translate('career.create.Details.LongDescriptionPlaceholder')}
          />
        </FormControl>
      </Box>
    </Box>
  );
};

Details.propTypes = {
  setIsStepValid: PropTypes.func.isRequired,
  newJob: PropTypes.object.isRequired,
  updateJobDetails: PropTypes.func.isRequired,
};

export default Details;
